import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TrendUp, TrendDown, Warning, ArrowSquareOut, Lightbulb, Robot, Globe, DeviceMobile, Desktop, Sparkle } from "@phosphor-icons/react";
import { fmtNum } from "./format";
import { SkeletonCard } from "./Skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_COLORS = {
  brand: "#FFEB00",
  transactional: "#34A853",
  commercial: "#4285F4",
  informational: "#A78BFA",
};

const INTENT_LABEL = {
  brand: "Brand",
  transactional: "Transactional",
  commercial: "Commercial",
  informational: "Informational",
};

const _trim = (url) => (url || "").replace(/^https?:\/\/[^/]+/, "") || "/";

/* ==================== WINNERS & LOSERS ==================== */

const WinnersLosersRow = ({ item, isPage, showDelta }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-b-0">
    <div className="text-xs text-white/85 truncate max-w-[70%]" title={isPage ? item.page : item.query}>
      {isPage ? (
        <a href={item.page} target="_blank" rel="noreferrer" className="hover:text-[#FFEB00] inline-flex items-center gap-1">
          {_trim(item.page)}
          <ArrowSquareOut size={9} className="opacity-50" />
        </a>
      ) : (
        <>
          {item.query}
          {item.branded && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-[#FFEB00]/20 text-[#FFEB00]">B</span>}
        </>
      )}
    </div>
    <div className={`text-xs font-semibold ${showDelta > 0 ? "text-emerald-300" : "text-rose-300"}`}>
      {showDelta > 0 ? "+" : ""}{fmtNum(showDelta)}
    </div>
  </div>
);

export const WinnersLosersSection = ({ data }) => {
  if (!data) return null;
  const { queries_gained = [], queries_lost = [], pages_gained = [], pages_lost = [] } = data;
  const hasAny = queries_gained.length + queries_lost.length + pages_gained.length + pages_lost.length > 0;
  if (!hasAny) return null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-winners-losers">
      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-3">Biggest Winners & Losers · vs previous period</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Queries */}
        <div className="space-y-3">
          <div>
            <div className="text-[11px] font-medium text-emerald-300 mb-1 flex items-center gap-1">
              <TrendUp size={12} /> Queries gaining clicks <span className="text-white/40 font-normal">({queries_gained.length})</span>
            </div>
            {queries_gained.length === 0
              ? <div className="text-[10px] text-white/40 py-1.5">No queries gained clicks in this window.</div>
              : queries_gained.map(q => <WinnersLosersRow key={"g_"+q.query} item={q} showDelta={q.delta_clicks} />)}
          </div>
          <div>
            <div className="text-[11px] font-medium text-rose-300 mb-1 flex items-center gap-1">
              <TrendDown size={12} /> Queries losing clicks <span className="text-white/40 font-normal">({queries_lost.length})</span>
            </div>
            {queries_lost.length === 0
              ? <div className="text-[10px] text-white/40 py-1.5">No queries lost clicks in this window.</div>
              : queries_lost.map(q => <WinnersLosersRow key={"l_"+q.query} item={q} showDelta={q.delta_clicks} />)}
          </div>
        </div>
        {/* Pages */}
        <div className="space-y-3">
          <div>
            <div className="text-[11px] font-medium text-emerald-300 mb-1 flex items-center gap-1">
              <TrendUp size={12} /> Pages gaining visibility <span className="text-white/40 font-normal">({pages_gained.length})</span>
            </div>
            {pages_gained.length === 0
              ? <div className="text-[10px] text-white/40 py-1.5">No pages gained visibility.</div>
              : pages_gained.map(p => <WinnersLosersRow key={"gp_"+p.page} item={p} isPage showDelta={p.delta_clicks} />)}
          </div>
          <div>
            <div className="text-[11px] font-medium text-rose-300 mb-1 flex items-center gap-1">
              <TrendDown size={12} /> Pages losing visibility <span className="text-white/40 font-normal">({pages_lost.length})</span>
            </div>
            {pages_lost.length === 0
              ? <div className="text-[10px] text-white/40 py-1.5">No pages lost visibility.</div>
              : pages_lost.map(p => <WinnersLosersRow key={"lp_"+p.page} item={p} isPage showDelta={p.delta_clicks} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== OPPORTUNITY KEYWORDS ==================== */

export const OpportunityKeywordsSection = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-[#FFEB0022] bg-gradient-to-r from-[#FFEB0008] to-transparent p-4" data-testid="gsc-opportunity">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb size={14} className="text-[#FFEB00]" />
        <span className="text-[10px] uppercase tracking-wider text-white/55">Opportunity Keywords</span>
        <span className="text-[10px] text-white/40">· rank 4–15 · impr ≥ 500 · below-average CTR</span>
      </div>
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Keyword</th>
              <th className="text-right px-3 py-2">Pos.</th>
              <th className="text-right px-3 py-2">Impr.</th>
              <th className="text-right px-3 py-2">CTR</th>
              <th className="text-right px-3 py-2">Expected</th>
              <th className="text-right px-3 py-2">Potential +</th>
              <th className="text-left px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(k => (
              <tr key={k.query} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-white/85 truncate max-w-[220px]" title={k.query}>{k.query}</td>
                <td className="px-3 py-2 text-right text-white/80">{k.position}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(k.impressions)}</td>
                <td className="px-3 py-2 text-right text-rose-300">{k.ctr}%</td>
                <td className="px-3 py-2 text-right text-emerald-300">{k.expected_ctr}%</td>
                <td className="px-3 py-2 text-right text-white font-semibold">+{fmtNum(k.potential_clicks)}</td>
                <td className="px-3 py-2 text-white/75 text-[11px]">{k.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-white/40 mt-2">
        Potential = clicks you&apos;d gain if CTR matched the industry median for that SERP position.
      </div>
    </div>
  );
};

/* ==================== CANNIBALIZATION ==================== */

export const CannibalizationSection = ({ items }) => {
  const [expanded, setExpanded] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-cannibalization">
      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">
        Keyword Cannibalization <span className="text-white/40 normal-case">· queries with ≥2 URLs competing</span>
      </div>
      <div className="space-y-1.5">
        {items.map((c, i) => (
          <div key={c.query} className="rounded border border-white/10 bg-white/[0.02]">
            <button
              type="button"
              onClick={() => setExpanded(expanded === i ? null : i)}
              className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-white/[0.02]"
              data-testid={`gsc-cannibal-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 shrink-0">{c.url_count} URLs</span>
                <span className="text-xs text-white/85 truncate">{c.query}</span>
                {c.branded && <span className="text-[9px] px-1 py-0.5 rounded bg-[#FFEB00]/20 text-[#FFEB00] shrink-0">B</span>}
              </div>
              <div className="flex items-center gap-3 text-[10px] text-white/55 shrink-0">
                <span>{fmtNum(c.total_impressions)} impr</span>
                <span>{fmtNum(c.total_clicks)} clicks</span>
                <span>{expanded === i ? "−" : "+"}</span>
              </div>
            </button>
            {expanded === i && (
              <div className="px-3 pb-2 border-t border-white/5">
                <table className="w-full text-xs mt-1">
                  <thead className="text-white/45 uppercase tracking-wider text-[9px]">
                    <tr>
                      <th className="text-left py-1">URL</th>
                      <th className="text-right py-1">Impr.</th>
                      <th className="text-right py-1">Clicks</th>
                      <th className="text-right py-1">CTR</th>
                      <th className="text-right py-1">Pos.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.urls.map((u, j) => (
                      <tr key={u.page + j}>
                        <td className="py-1 text-white/80 truncate max-w-[300px]">
                          <a href={u.page} target="_blank" rel="noreferrer" className="hover:text-[#FFEB00]">{_trim(u.page)}</a>
                        </td>
                        <td className="py-1 text-right text-white/70">{fmtNum(u.impressions)}</td>
                        <td className="py-1 text-right text-white/70">{fmtNum(u.clicks)}</td>
                        <td className="py-1 text-right text-white/70">{u.ctr}%</td>
                        <td className="py-1 text-right text-white/70">{u.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================== KEYWORD INTENT SPLIT ==================== */

export const IntentSplitSection = ({ intent }) => {
  const [activeBucket, setActiveBucket] = useState(null);
  if (!intent || !intent.buckets) return null;
  const buckets = intent.buckets;
  const totalClicks = buckets.reduce((s, b) => s + b.clicks, 0);
  if (totalClicks === 0) return null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-intent-split">
      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-3">Keyword Intent Split</div>

      {/* Stacked bar */}
      <div className="flex h-6 rounded overflow-hidden mb-3">
        {buckets.map(b => {
          const pct = totalClicks > 0 ? (b.clicks / totalClicks * 100) : 0;
          if (pct === 0) return null;
          return (
            <button
              key={b.intent}
              type="button"
              onClick={() => setActiveBucket(activeBucket === b.intent ? null : b.intent)}
              className="relative group transition-opacity hover:opacity-80"
              style={{ width: `${pct}%`, background: INTENT_COLORS[b.intent] }}
              data-testid={`gsc-intent-bar-${b.intent}`}
              title={`${INTENT_LABEL[b.intent]}: ${b.clicks} clicks (${pct.toFixed(1)}%)`}
            >
              {pct >= 8 && (
                <span className="text-[9px] text-black font-semibold px-1 whitespace-nowrap">{pct.toFixed(0)}%</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend + counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {buckets.map(b => (
          <button
            key={b.intent}
            type="button"
            onClick={() => setActiveBucket(activeBucket === b.intent ? null : b.intent)}
            className={`text-left rounded p-2 border transition-colors ${activeBucket === b.intent ? "border-white/30 bg-white/[0.05]" : "border-white/10 hover:border-white/20"}`}
            data-testid={`gsc-intent-card-${b.intent}`}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: INTENT_COLORS[b.intent] }} />
              <span className="text-[11px] text-white/85 font-medium">{INTENT_LABEL[b.intent]}</span>
            </div>
            <div className="text-lg font-semibold text-white leading-tight" style={{ fontFamily: "Varela Round, sans-serif" }}>
              {fmtNum(b.clicks, { compact: true })}
            </div>
            <div className="text-[10px] text-white/50">{b.queries} queries · {fmtNum(b.impressions, { compact: true })} impr</div>
          </button>
        ))}
      </div>

      {/* Top queries in active bucket */}
      {activeBucket && intent.top_by_intent[activeBucket]?.length > 0 && (
        <div className="border-t border-white/10 pt-2">
          <div className="text-[10px] text-white/50 mb-1.5">Top {INTENT_LABEL[activeBucket]} queries</div>
          <div className="space-y-1">
            {intent.top_by_intent[activeBucket].slice(0, 10).map(q => (
              <div key={q.query} className="flex items-center justify-between text-xs py-0.5">
                <span className="text-white/80 truncate max-w-[70%]">{q.query}</span>
                <span className="text-white/55">{fmtNum(q.clicks)} clk · pos {q.position}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================== CATEGORY PERFORMANCE ==================== */

export const CategoryPerformanceSection = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-category-perf">
      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">
        Category Performance <span className="text-white/40 normal-case">· grouped by URL path / collection</span>
      </div>
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-right px-3 py-2">Pages</th>
              <th className="text-right px-3 py-2">Clicks</th>
              <th className="text-right px-3 py-2">Impr.</th>
              <th className="text-right px-3 py-2">CTR</th>
              <th className="text-right px-3 py-2">Avg Pos.</th>
              <th className="text-right px-3 py-2">Δ Clicks</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.collection} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-white/85 font-medium">{c.collection}</td>
                <td className="px-3 py-2 text-right text-white/70">{c.page_count}</td>
                <td className="px-3 py-2 text-right text-white">{fmtNum(c.clicks)}</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(c.impressions)}</td>
                <td className="px-3 py-2 text-right text-white/80">{c.ctr}%</td>
                <td className="px-3 py-2 text-right text-white/80">{c.position}</td>
                <td className="px-3 py-2 text-right">
                  {c.delta_clicks_pct != null ? (
                    <span className={c.delta_clicks_pct >= 0 ? "text-emerald-300" : "text-rose-300"}>
                      {c.delta_clicks_pct >= 0 ? "+" : ""}{c.delta_clicks_pct}%
                    </span>
                  ) : <span className="text-white/30">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ==================== DEVICE DEEP SPLIT (Mobile vs Desktop per query) ==================== */

export const DeviceDeepSplitSection = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-device-deep-split">
      <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">
        Mobile vs Desktop · per query
      </div>
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th rowSpan={2} className="text-left px-3 py-2 align-bottom">Query</th>
              <th colSpan={3} className="text-center px-3 py-1 border-l border-white/10">
                <div className="flex items-center justify-center gap-1"><DeviceMobile size={11} /> Mobile</div>
              </th>
              <th colSpan={3} className="text-center px-3 py-1 border-l border-white/10">
                <div className="flex items-center justify-center gap-1"><Desktop size={11} /> Desktop</div>
              </th>
              <th rowSpan={2} className="text-right px-3 py-2 align-bottom border-l border-white/10">Pos Gap</th>
            </tr>
            <tr>
              <th className="text-right px-2 py-1 border-l border-white/10 font-normal">Clicks</th>
              <th className="text-right px-2 py-1 font-normal">CTR</th>
              <th className="text-right px-2 py-1 font-normal">Pos.</th>
              <th className="text-right px-2 py-1 border-l border-white/10 font-normal">Clicks</th>
              <th className="text-right px-2 py-1 font-normal">CTR</th>
              <th className="text-right px-2 py-1 font-normal">Pos.</th>
            </tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d.query} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-white/85 truncate max-w-[220px]" title={d.query}>{d.query}</td>
                <td className="px-2 py-2 text-right text-white/80 border-l border-white/5">{fmtNum(d.mobile.clicks)}</td>
                <td className="px-2 py-2 text-right text-white/80">{d.mobile.ctr}%</td>
                <td className="px-2 py-2 text-right text-white/80">{d.mobile.position}</td>
                <td className="px-2 py-2 text-right text-white/80 border-l border-white/5">{fmtNum(d.desktop.clicks)}</td>
                <td className="px-2 py-2 text-right text-white/80">{d.desktop.ctr}%</td>
                <td className="px-2 py-2 text-right text-white/80">{d.desktop.position}</td>
                <td className="px-2 py-2 text-right border-l border-white/5">
                  {d.position_gap != null ? (
                    <span className={d.position_gap > 0 ? "text-rose-300" : "text-emerald-300"}>
                      {d.position_gap > 0 ? "+" : ""}{d.position_gap}
                    </span>
                  ) : <span className="text-white/30">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-white/40 mt-2">
        Pos gap = mobile pos − desktop pos. Positive (red) means the query ranks WORSE on mobile.
      </div>
    </div>
  );
};

/* ==================== SEARCH APPEARANCE (AI Overviews, snippets, rich results) ==================== */

export const SearchAppearanceSection = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-search-appearance-empty">
        <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Search Appearance & AI Overviews</div>
        <div className="text-xs text-white/45 py-2">
          No enhanced search appearance data in this window. Your site may not yet have any rich results, AI Overview citations, or structured-data-driven snippets.
          <br /><span className="text-[10px] text-white/35">Google shows this data only when a site has qualifying rich-result markup or is cited in AI Overviews.</span>
        </div>
      </div>
    );
  }
  const aiRow = items.find(i => i.is_ai_overview);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-search-appearance">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-wider text-white/50">
          Search Appearance <span className="text-white/40 normal-case">· how your pages surface on Google (incl. AI Overviews)</span>
        </div>
        {aiRow && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-[#A78BFA22] text-[#A78BFA] font-semibold" data-testid="gsc-ai-overview-highlight">
            ✨ Cited by AI Overviews: {fmtNum(aiRow.clicks)} clicks · {fmtNum(aiRow.impressions)} impr
          </span>
        )}
      </div>
      <div className="overflow-x-auto rounded border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/55 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Appearance Type</th>
              <th className="text-right px-3 py-2">Clicks</th>
              <th className="text-right px-3 py-2">Share</th>
              <th className="text-right px-3 py-2">Impressions</th>
              <th className="text-right px-3 py-2">CTR</th>
              <th className="text-right px-3 py-2">Avg Pos.</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.code} className={`border-t border-white/5 ${r.is_ai_overview ? "bg-[#A78BFA]/[0.05]" : "hover:bg-white/[0.02]"}`}>
                <td className="px-3 py-2 text-white/85">
                  {r.is_ai_overview && <span className="mr-1.5 text-[#A78BFA]">✨</span>}
                  {r.label}
                  {r.is_ai_overview && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-[#A78BFA]/20 text-[#A78BFA] font-semibold">AI</span>}
                </td>
                <td className="px-3 py-2 text-right text-white font-medium">{fmtNum(r.clicks)}</td>
                <td className="px-3 py-2 text-right text-white/70">{r.share_of_clicks}%</td>
                <td className="px-3 py-2 text-right text-white/80">{fmtNum(r.impressions)}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.ctr}%</td>
                <td className="px-3 py-2 text-right text-white/80">{r.position}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-white/40 mt-2">
        Source: GSC <code className="text-white/55">searchAppearance</code> dimension. Values include AI Overviews (Google&apos;s generative-answer boxes), product/review/FAQ snippets, sitelinks, image/video results, and more.
      </div>
    </div>
  );
};

/* ==================== SEARCH APPEARANCE - END ==================== */



export const CountryDrillSection = ({ clientId, countries, dateRange }) => {
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async (cc) => {
    if (!cc || !dateRange?.start_date || !dateRange?.end_date) return;
    setLoading(true); setErr(""); setDrill(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/country-drill`, {
        params: { country: cc, start_date: dateRange.start_date, end_date: dateRange.end_date }
      });
      setDrill(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, dateRange?.start_date, dateRange?.end_date]);

  useEffect(() => {
    if (!selected && countries && countries.length > 0) setSelected(countries[0].country);
  }, [countries, selected]);

  useEffect(() => { if (selected) load(selected); }, [selected, load]);

  if (!countries || countries.length === 0) return null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="gsc-country-drill">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-wider text-white/50">
          <Globe size={11} className="inline mr-1 -mt-0.5" /> Country Deep Dive
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {countries.slice(0, 8).map(c => (
            <button
              type="button"
              key={c.country}
              onClick={() => setSelected(c.country)}
              className={`text-[10px] px-2 py-0.5 rounded border ${selected === c.country ? "bg-[#FFEB00] text-black border-[#FFEB00] font-semibold" : "bg-white/[0.04] text-white/60 border-white/15 hover:border-white/30"}`}
              data-testid={`gsc-country-btn-${c.country}`}
            >
              {c.country.toUpperCase()} · {fmtNum(c.clicks, { compact: true })}
            </button>
          ))}
        </div>
      </div>

      {loading && <SkeletonCard rows={3} />}
      {err && <div className="text-xs text-red-300">{err}</div>}
      {drill && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-white/50 mb-1.5">Top Queries · {selected?.toUpperCase()}</div>
            <div className="space-y-0.5">
              {drill.queries.length === 0
                ? <div className="text-[11px] text-white/40 py-2">No queries.</div>
                : drill.queries.slice(0, 10).map(q => (
                <div key={q.query} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-white/85 truncate max-w-[70%]">{q.query}</span>
                  <span className="text-white/55 text-[10px]">{fmtNum(q.clicks)} clk · pos {q.position}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-white/50 mb-1.5">Top Landing Pages · {selected?.toUpperCase()}</div>
            <div className="space-y-0.5">
              {drill.pages.length === 0
                ? <div className="text-[11px] text-white/40 py-2">No pages.</div>
                : drill.pages.slice(0, 10).map(p => (
                <div key={p.page} className="flex items-center justify-between text-xs py-0.5">
                  <a href={p.page} target="_blank" rel="noreferrer" className="text-white/85 truncate max-w-[70%] hover:text-[#FFEB00]">{_trim(p.page)}</a>
                  <span className="text-white/55 text-[10px]">{fmtNum(p.clicks)} clk</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ==================== AI SEARCH READINESS ==================== */

export const AiSearchReadinessSection = ({ clientId, dateRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const sd = dateRange?.start_date;
  const ed = dateRange?.end_date;

  const load = useCallback(async () => {
    if (!sd || !ed) return;
    setLoading(true); setErr(""); setData(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/search-console/ai-readiness`, { params: { start_date: sd, end_date: ed } });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, sd, ed]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <SkeletonCard rows={3} />;
  if (err) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-xs text-white/60" data-testid="gsc-ai-readiness-err">
        AI Search Readiness — {err}
      </div>
    );
  }
  if (!data) return null;
  if (!data.configured) {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex items-center gap-2" data-testid="gsc-ai-readiness-not-configured">
        <Robot size={16} className="text-white/50" />
        <div className="text-xs text-white/65">
          <div className="font-medium">AI Search Readiness</div>
          <div className="text-white/45">{data.message || "GA4 not connected — needed to track referrals from ChatGPT, Perplexity, Gemini, Claude, etc."}</div>
        </div>
      </div>
    );
  }
  const { assistants = [], top_pages = [], totals = {} } = data;
  const hasAny = assistants.length > 0;
  return (
    <div className="rounded-lg border border-[#A78BFA22] bg-gradient-to-r from-[#A78BFA11] to-transparent p-4" data-testid="gsc-ai-readiness">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={14} className="text-[#A78BFA]" />
        <span className="text-[10px] uppercase tracking-wider text-white/55">AI Search Readiness</span>
        <span className="text-[10px] text-white/40">· traffic from ChatGPT / Perplexity / Gemini / Claude / etc.</span>
      </div>
      {!hasAny ? (
        <div className="text-xs text-white/55 py-4 text-center">
          No detectable AI-assistant referrals in this window.<br/>
          <span className="text-[10px] text-white/40">This is normal for new sites or short windows. AI tools like ChatGPT & Perplexity are just starting to send referrer data.</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="rounded border border-white/10 bg-white/[0.02] p-2">
              <div className="text-[10px] uppercase text-white/45">Total AI Sessions</div>
              <div className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>{fmtNum(totals.sessions || 0)}</div>
            </div>
            <div className="rounded border border-white/10 bg-white/[0.02] p-2">
              <div className="text-[10px] uppercase text-white/45">Unique Users</div>
              <div className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>{fmtNum(totals.users || 0)}</div>
            </div>
            <div className="rounded border border-white/10 bg-white/[0.02] p-2">
              <div className="text-[10px] uppercase text-white/45">Assistants</div>
              <div className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>{assistants.length}</div>
            </div>
            <div className="rounded border border-white/10 bg-white/[0.02] p-2">
              <div className="text-[10px] uppercase text-white/45">Cited Pages</div>
              <div className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>{top_pages.length}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Per-assistant */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">By Assistant</div>
              <div className="space-y-1">
                {assistants.map(a => (
                  <div key={a.assistant} className="rounded border border-white/10 bg-white/[0.02] p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white font-medium">{a.assistant}</span>
                      <span className="text-[10px] text-white/60">{fmtNum(a.sessions)} sessions · {fmtNum(a.users)} users</span>
                    </div>
                    {a.top_pages.length > 0 && (
                      <div className="space-y-0.5">
                        {a.top_pages.map(p => (
                          <div key={p.page} className="flex items-center justify-between text-[10px]">
                            <a href={p.page} target="_blank" rel="noreferrer" className="text-white/70 truncate max-w-[70%] hover:text-[#FFEB00]">{_trim(p.page)}</a>
                            <span className="text-white/45">{fmtNum(p.sessions)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Top pages */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Pages Getting AI Traffic</div>
              <div className="space-y-0.5">
                {top_pages.map(p => (
                  <div key={p.page} className="flex items-center justify-between text-[11px] py-0.5">
                    <a href={p.page} target="_blank" rel="noreferrer" className="text-white/80 truncate max-w-[55%] hover:text-[#FFEB00]">{_trim(p.page)}</a>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5">
                        {p.assistants.map(a => (
                          <span key={a} className="text-[9px] px-1 py-0.5 rounded bg-[#A78BFA]/20 text-[#A78BFA]" title={a}>{a[0]}</span>
                        ))}
                      </div>
                      <span className="text-white/55 text-[10px]">{fmtNum(p.sessions)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
